import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Return-Typ des useFeatureFlag Hooks
 */
export interface UseFeatureFlagResult {
  /** Der aktuelle enabled-Status (DB-Wert oder staticDefault) */
  enabled: boolean;
  /** Ob der DB-Wert noch geladen wird */
  isLoading: boolean;
}

/**
 * Hook für Feature-Flag-Tracking mit Sentry Integration und DB-Support.
 *
 * Nutzt TanStack Query mit shared queryKey ['feature-flags'], sodass
 * alle Instanzen denselben gecachten Request teilen (~10 Requests → 1).
 *
 * PRIORITÄT:
 * 1. DB-Wert aus system_settings (key: 'feature_flags') - dynamisch änderbar
 * 2. staticDefault Parameter - Code-Default als Fallback
 *
 * @param flagName - Der Name des Feature-Flags (kebab-case)
 * @param staticDefault - Der statische Default-Wert aus FEATURE_FLAGS
 * @returns Objekt mit enabled-Status und isLoading-Flag
 *
 * @see .lovable/memory/features/dynamic-feature-flag-system.md
 */
/**
 * Network timeout for the feature-flag fetch.
 *
 * The flag fetch is on the critical path of HomeRoute (`loading || flagLoading`):
 * while it is pending, the app shows the full-screen CheckMatyLoader. The
 * Supabase/PostgREST request has no built-in timeout, so on a stalled mobile
 * connection ("Lie-Fi") it can hang forever, leaving the user on an eternal
 * splash that never reaches the landing/auth page. We bound it with an
 * AbortController so the query always settles and falls back to cached/default
 * flags. Uses a manual controller (not AbortSignal.timeout) to mirror
 * connectivityService and to stay testable with fake timers.
 */
const FLAG_FETCH_TIMEOUT_MS = 8_000;

function createTimeoutSignal(ms: number): { signal: AbortSignal; clear: () => void } {
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(new DOMException("Feature-flag fetch timed out", "TimeoutError")),
    ms,
  );
  return { signal: controller.signal, clear: () => clearTimeout(timer) };
}

export function useFeatureFlag(flagName: string, staticDefault: boolean): UseFeatureFlagResult {
  const CACHE_KEY = "feature-flags-cache";

  const { data: flags, isLoading } = useQuery({
    queryKey: ["feature-flags"],
    queryFn: async () => {
      // Clear the timer once the query settles (success, error or timeout) so a
      // fast response never leaves a dangling timeout running.
      const timeout = createTimeoutSignal(FLAG_FETCH_TIMEOUT_MS);
      try {
        const { data, error } = await supabase
          .from("system_settings")
          .select("value")
          .eq("key", "feature_flags")
          .abortSignal(timeout.signal)
          .maybeSingle();

        if (!error && data?.value) {
          const flagValues = data.value as Record<string, boolean>;
          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify(flagValues));
          } catch {
            /* localStorage full – ignore */
          }
          return flagValues;
        }
      } catch (err) {
        console.warn(`[useFeatureFlag] Failed to load flags from DB:`, err);
      } finally {
        timeout.clear();
      }
      // Offline fallback: use last known values from localStorage
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) return JSON.parse(cached) as Record<string, boolean>;
      } catch {
        /* corrupt cache – ignore */
      }
      return {} as Record<string, boolean>;
    },
    // Sofortige Initialdaten aus localStorage, damit flags nie undefined ist
    // und offlineEnabled beim ersten Render sofort korrekt ist
    initialData: () => {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) return JSON.parse(cached) as Record<string, boolean>;
      } catch {
        /* ignore */
      }
      return undefined;
    },
    staleTime: 60 * 1000,
    retry: false, // Don't retry on network errors (offline)
  });

  // DB-Wert hat Priorität, sonst statischer Default
  const enabled = flags?.[flagName] ?? staticDefault;

  // Sentry-Tracking
  useEffect(() => {
    const client = Sentry.getClient();
    if (client) {
      // The FeatureFlags integration exposes `addFeatureFlag` at runtime, but the
      // base Integration type does not declare it — narrow structurally instead of `any`.
      const integration = client.getIntegrationByName("FeatureFlags") as
        | { addFeatureFlag?: (name: string, value: boolean) => void }
        | undefined;
      if (integration && typeof integration.addFeatureFlag === "function") {
        integration.addFeatureFlag(flagName, enabled);
      }
    }
  }, [flagName, enabled]);

  return { enabled, isLoading };
}
